<template>
  <div class="invoice-products">
    <!-- First Invoice -->
    <invoice-period-table
      :key="'first-invoice-' + startDate"
      :title="'Eerste factuur (' + formatFirstInvoicePeriod() + ')'"
      :lines="firstInvoiceLines"
      :showBTW="showBTWforCustomers"
      :show-amount="showAmountColumn"
    />

    <!-- Follow-up Invoice -->
    <invoice-period-table
      v-if="shouldShowFollowUp"
      :key="'follow-up-invoice-' + startDate"
      :title="'Vervolg factuur (' + formatFollowUpDate() + ')'"
      :lines="followUpInvoiceLines"
      :showBTW="showBTWforCustomers"
      :show-amount="showAmountColumn"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import InvoicePeriodTable from './components/InvoicePeriodTable.vue';
import moment from 'moment';

export default {
  name: 'ContractsRecordInvoiceProducts',

  components: {
    InvoicePeriodTable
  },

  props: {
    record: {
      type: Object,
      required: true
    },
    contractExpirationDate: {
      type: Object,
      default: null,
      required: false
    },
    customerType: {
      type: String,
      required: true
    },
    products: {
      type: Object,
      required: true
    },
    startDate: {
      type: Date,
      required: false,
      default: null
    },
    invoiceType: {
      type: String,
      required: true
    },
    showBTWforCustomers: {
      type: Boolean,
      default: true
    },
    invoice_freq: {
      type: String,
      default: 'month'
    }
  },

  data() {
    return {};
  },

  computed: {
    ...mapState('invoice', {
      calculatedLines: state => state.calculatedLines
    }),

    shouldShowFollowUp() {
      return _.isNull(this.contractExpirationDate) || this.record?.step1['length'] > 1;
    },

    firstInvoiceLines() {
      return this.calculatedLines.firstInvoice || [];
    },

    followUpInvoiceLines() {
      return this.calculatedLines.followUpInvoice || [];
    },

    showAmountColumn() {
      return true
    }
  },

  watch: {
    products: {
      immediate: true,
      handler() {
        if (!this.startDate || !this.customerType) {
          console.log('Missing required data for calculation');
          return;
        }

        // Calculate first invoice
        this.$store.dispatch('invoice/calculateInvoice', {
          contract: {
            start_date: this.startDate,
            invoice_freq: this.invoice_freq,
            customer: { type: this.customerType },
            is_follow_up: false
          },
          boxes: this.products.boxes || [],
          products: this.products.products || []
        });

        // Calculate follow-up invoice if needed
        if (this.shouldShowFollowUp) {
          this.$store.dispatch('invoice/calculateInvoice', {
            contract: {
              start_date: this.startDate,
              invoice_freq: this.invoice_freq,
              customer: { type: this.customerType },
              is_follow_up: true
            },
            boxes: this.products.boxes || [],
            products: this.products.products || []
          });
        }
      }
    }
  },

  methods: {
    formatPrice(value) {
      return Number(value || 0).toFixed(2);
    },

    formatDate(date) {
      return moment(date).format('DD-MM-YYYY');
    },

    formatFirstInvoicePeriod() {
      const date = moment(this.startDate);
      const frequency = this.invoice_freq;
      const isPre15 = date.date() < 15;

      // Calculate end date based on frequency and pre15/after15
      let endDate;
      if (frequency === 'year') {
        // For yearly: remaining days + 11/12 months
        endDate = date.clone()
          .add(isPre15 ? 11 : 12, 'months')
          .endOf('month');
      } else if (frequency === 'halfyear') {
        // For halfyearly: remaining days + 5/6 months
        endDate = date.clone()
          .add(isPre15 ? 5 : 6, 'months')
          .endOf('month');
      } else if (frequency === 'quarter') {
        // For quarterly: remaining days + 2/3 months
        endDate = date.clone()
          .add(isPre15 ? 2 : 3, 'months')
          .endOf('month');
      } else {
        // For monthly: remaining days + 1 month if after15
        endDate = date.clone()
          .add(isPre15 ? 0 : 1, 'months')
          .endOf('month');
      }

      return `${date.format('DD-MM-YYYY')} t/m ${endDate.format('DD-MM-YYYY')}`;
    },

    formatFollowUpDate() {
      const date = moment(this.startDate);
      const frequency = this.invoice_freq;
      const isPre15 = date.date() < 15;

      let startDate;
      if (frequency === 'year') {
        // For yearly contracts:
        // - pre15: first invoice has remaining days + 11 months, so follow-up starts after 12 months
        // - after15: first invoice has remaining days + 12 months, so follow-up starts after 13 months
        startDate = date.clone()
          .add(isPre15 ? 12 : 13, 'months')
          .startOf('month');
      } else if (frequency === 'quarter') {
        // For quarterly contracts:
        // - pre15: first invoice has remaining days + 2 months, so follow-up starts after 3 months
        // - after15: first invoice has remaining days + 3 months, so follow-up starts after 4 months
        startDate = date.clone()
          .add(isPre15 ? 3 : 4, 'months')
          .startOf('month');
      } else if (frequency === 'halfyear') {
        // For halfyearly contracts:
        // - pre15: first invoice has remaining days + 5 months, so follow-up starts after 6 months
        // - after15: first invoice has remaining days + 6 months, so follow-up starts after 7 months
        startDate = date.clone()
          .add(isPre15 ? 6 : 7, 'months')
          .startOf('month');
      } else {
        // For monthly contracts:
        // - pre15: first invoice has remaining days, so follow-up starts after 1 month
        // - after15: first invoice has remaining days + 1 month, so follow-up starts after 2 months
        startDate = date.clone()
          .add(isPre15 ? 1 : 2, 'months')
          .startOf('month');
      }

      const months = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1;
      const endDate = startDate.clone().add(months - 1, 'months').endOf('month');

      return `${startDate.format('DD-MM-YYYY')} t/m ${endDate.format('DD-MM-YYYY')}`;
    }
  }
};
</script>

<style scoped>
.invoice-products {
  width: 100%;
}
</style>